/**
 * sales-preview-api — 판매 현황 시안의 API(10-28 회의용).
 *
 * 백엔드 `modules/sales-preview/SalesPreview.Types.ts`(hcltech-parts)를 Zod 로 비춘다. 이카운트 판매 임시 표(tmp_ecount_sale · _line)를 읽는다.
 * 시연 뒤 표와 같이 지운다(백엔드 V장 "판매 현황" 의 "10-28 회의 시안").
 */
import { z } from 'zod';
import { getJson } from './http';

const BASE = '/api/admin/sales-preview';
export const SALES_PREVIEW_LIMIT = 200;   // 판매 127 건 — 한 쪽에 다 들어온다(백엔드 최대와 같다)

/** 목록 카드 둘째 줄의 품목 한 줄 */
export const SalesPreviewItemSchema = z.strictObject({
  part_number:    z.string().nullable(),
  spec:           z.string().nullable(),
  condition_name: z.string().nullable(),
  quantity:       z.number().nullable(),
});

/** 판매 한 건 = 목록 카드 하나 */
export const SalesPreviewViewSchema = z.strictObject({
  slip_no:            z.string(),                 // 이카운트 일자-No.
  sold_date:          z.string(),                 // 'YYYY-MM-DD'
  customer_name:      z.string().nullable(),
  manager_name:       z.string().nullable(),
  progress_status:    z.string().nullable(),      // 이카운트 글 그대로
  total_amount:       z.number().nullable(),      // 금액합계(부가세 포함)
  supply_amount:      z.number().nullable(),      // 공급가액 합
  cost_amount:        z.number().nullable(),      // 추정 원가 합 — 찾은 줄만
  margin_amount:      z.number().nullable(),      // 원가 찾은 줄의 공급가액 − 원가
  cost_missing_count: z.number().int().nonnegative(),
  line_count:         z.number().int().nonnegative(),
  items:              z.array(SalesPreviewItemSchema),
});

/** 한 건 상세의 품목 한 줄 */
export const SalesPreviewLineSchema = z.strictObject({
  line_seq:            z.number().int(),
  item_group:          z.string().nullable(),
  vendor_name:         z.string().nullable(),
  part_number:         z.string().nullable(),
  spec:                z.string().nullable(),
  condition_name:      z.string().nullable(),
  quantity:            z.number().nullable(),
  unit_price:          z.number().nullable(),
  supply_amount:       z.number().nullable(),
  cost_unit_price:     z.number().nullable(),
  cost_slip_no:        z.string().nullable(),
  cost_purchased_date: z.string().nullable(),
  margin_amount:       z.number().nullable(),
});

export const SalesPreviewDetailSchema = SalesPreviewViewSchema.extend({ lines: z.array(SalesPreviewLineSchema) });

export const SalesPreviewViewListSchema = z.strictObject({
  rows:   z.array(SalesPreviewViewSchema),
  total:  z.number().int().nonnegative(),
  limit:  z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export type SalesPreviewItem = z.infer<typeof SalesPreviewItemSchema>;
export type SalesPreviewView = z.infer<typeof SalesPreviewViewSchema>;
export type SalesPreviewLine = z.infer<typeof SalesPreviewLineSchema>;
export type SalesPreviewDetail = z.infer<typeof SalesPreviewDetailSchema>;
export type SalesPreviewViewList = z.infer<typeof SalesPreviewViewListSchema>;

/** 목록 질의 — 찾기(거래처 · 담당자 · 파트넘버) · 진행상태(이카운트 글, '' = 전부) */
export type SalesPreviewListQuery = { q: string; status: string };

export async function fetchSalesPreviews(query: SalesPreviewListQuery): Promise<SalesPreviewViewList> {
  const params = new URLSearchParams({ limit: String(SALES_PREVIEW_LIMIT), offset: '0' });
  if (query.q !== '') params.set('q', query.q);
  if (query.status !== '') params.set('status', query.status);
  return getJson(`${BASE}?${params.toString()}`, { schema: SalesPreviewViewListSchema });
}

/** 한 건 — 일자-No.에 `/` 가 들어 경로가 아니라 질의로 보낸다 */
export async function fetchSalesPreview(slipNo: string): Promise<SalesPreviewDetail> {
  const params = new URLSearchParams({ slip_no: slipNo });
  return getJson(`${BASE}/detail?${params.toString()}`, { schema: SalesPreviewDetailSchema });
}
